import { create } from 'zustand';
import type { ChatMessage, ChatParticipant, ChatConversation } from '@/services/chatQueries';

export type WsStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

interface WsFns {
  sendMessage: (convId: string, text: string) => void;
  editMessage: (msgId: string, convId: string, text: string) => void;
  deleteMessage: (msgId: string, convId: string) => void;
  markRead: (convId: string, msgId: string) => void;
}

interface ChatStore {
  // ── Global room ────────────────────────────────────────────────────────────
  globalConversationId: string | null;
  globalMessages: ChatMessage[];
  globalHasMore: boolean;
  globalUnread: number;
  isPanelOpen: boolean;

  // ── Participants (merged from all conversations) ───────────────────────────
  participants: Record<string, ChatParticipant>;

  // ── Direct messages ────────────────────────────────────────────────────────
  directConversations: ChatConversation[];
  activeDirectId: string | null;
  directMessages: Record<string, ChatMessage[]>;
  directHasMore: Record<string, boolean>;
  directUnread: Record<string, number>;

  // ── WS ────────────────────────────────────────────────────────────────────
  wsStatus: WsStatus;
  wsFns: WsFns | null;

  // ── Actions: global messages ───────────────────────────────────────────────
  setGlobalConversationId: (id: string) => void;
  setGlobalMessages: (msgs: ChatMessage[]) => void;
  prependGlobalMessages: (msgs: ChatMessage[]) => void;
  appendGlobalMessage: (msg: ChatMessage) => void;
  replaceMessage: (tempId: string, real: ChatMessage) => void;
  upsertGlobalMessage: (msg: ChatMessage) => void;
  tombstoneMessage: (messageId: string, deletedAt: string) => void;
  addReadReceipt: (messageId: string, userId: string, readAt: string) => void;
  removeMessage: (id: string) => void;
  setGlobalHasMore: (v: boolean) => void;

  // ── Actions: participants ──────────────────────────────────────────────────
  mergeParticipants: (map: Record<string, ChatParticipant>) => void;
  /** @deprecated use mergeParticipants */
  setParticipants: (map: Record<string, ChatParticipant>) => void;

  // ── Actions: unread ────────────────────────────────────────────────────────
  setGlobalUnread: (n: number) => void;
  incrementGlobalUnread: () => void;
  clearGlobalUnread: () => void;

  // ── Actions: panel ────────────────────────────────────────────────────────
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;

  // ── Actions: direct conversations ─────────────────────────────────────────
  setDirectConversations: (convs: ChatConversation[]) => void;
  upsertDirectConversation: (conv: ChatConversation) => void;
  setActiveDirectId: (id: string | null) => void;

  // ── Actions: direct messages ───────────────────────────────────────────────
  setDirectMessages: (convId: string, msgs: ChatMessage[]) => void;
  prependDirectMessages: (convId: string, msgs: ChatMessage[]) => void;
  appendDirectMessage: (convId: string, msg: ChatMessage) => void;
  replaceDirectMessage: (convId: string, tempId: string, real: ChatMessage) => void;
  upsertDirectMessage: (convId: string, msg: ChatMessage) => void;
  tombstoneDirectMessage: (convId: string, messageId: string, deletedAt: string) => void;
  addDirectReadReceipt: (convId: string, messageId: string, userId: string, readAt: string) => void;
  removeDirectMessage: (convId: string, id: string) => void;
  setDirectHasMore: (convId: string, v: boolean) => void;

  // ── Actions: direct unread ─────────────────────────────────────────────────
  setDirectUnread: (convId: string, n: number) => void;
  incrementDirectUnread: (convId: string) => void;
  clearDirectUnread: (convId: string) => void;

  // ── Actions: WS ───────────────────────────────────────────────────────────
  setWsStatus: (s: WsStatus) => void;
  setWsFns: (fns: WsFns) => void;

  // ── Reset (on logout) ─────────────────────────────────────────────────────
  resetStore: () => void;
}

export const useChatStore = create<ChatStore>((set, _get) => ({
  globalConversationId: null,
  globalMessages: [],
  globalHasMore: false,
  globalUnread: 0,
  isPanelOpen: false,
  participants: {},
  directConversations: [],
  activeDirectId: null,
  directMessages: {},
  directHasMore: {},
  directUnread: {},
  wsStatus: 'idle',
  wsFns: null,

  // Global messages
  setGlobalConversationId: (id) => set({ globalConversationId: id }),
  setGlobalMessages: (msgs) => set({ globalMessages: msgs }),
  prependGlobalMessages: (msgs) => set((s) => ({ globalMessages: [...msgs, ...s.globalMessages] })),
  appendGlobalMessage: (msg) => set((s) => ({ globalMessages: [...s.globalMessages, msg] })),
  replaceMessage: (tempId, real) =>
    set((s) => ({ globalMessages: s.globalMessages.map((m) => (m.id === tempId ? real : m)) })),
  upsertGlobalMessage: (msg) =>
    set((s) => ({ globalMessages: s.globalMessages.map((m) => (m.id === msg.id ? msg : m)) })),
  tombstoneMessage: (messageId, deletedAt) =>
    set((s) => ({
      globalMessages: s.globalMessages.map((m) =>
        m.id === messageId ? { ...m, isDeleted: true, text: null, deletedAt } : m
      ),
    })),
  addReadReceipt: (messageId, userId, readAt) =>
    set((s) => ({
      globalMessages: s.globalMessages.map((m) =>
        m.id === messageId
          ? { ...m, readBy: [...m.readBy.filter((r) => r.userId !== userId), { userId, readAt }] }
          : m
      ),
    })),
  removeMessage: (id) =>
    set((s) => ({ globalMessages: s.globalMessages.filter((m) => m.id !== id) })),
  setGlobalHasMore: (v) => set({ globalHasMore: v }),

  // Participants
  mergeParticipants: (map) => set((s) => ({ participants: { ...s.participants, ...map } })),
  setParticipants: (map) => set((s) => ({ participants: { ...s.participants, ...map } })),

  // Unread
  setGlobalUnread: (n) => set({ globalUnread: n }),
  incrementGlobalUnread: () => set((s) => ({ globalUnread: s.globalUnread + 1 })),
  clearGlobalUnread: () => set({ globalUnread: 0 }),

  // Panel
  openPanel: () => set({ isPanelOpen: true }),
  closePanel: () => set({ isPanelOpen: false }),
  togglePanel: () => set((s) => ({ isPanelOpen: !s.isPanelOpen })),

  // Direct conversations
  setDirectConversations: (convs) => set({ directConversations: convs }),
  upsertDirectConversation: (conv) =>
    set((s) => {
      const exists = s.directConversations.find((c) => c.id === conv.id);
      return {
        directConversations: exists
          ? s.directConversations.map((c) => (c.id === conv.id ? conv : c))
          : [conv, ...s.directConversations],
      };
    }),
  setActiveDirectId: (id) => set({ activeDirectId: id }),

  // Direct messages
  setDirectMessages: (convId, msgs) =>
    set((s) => ({ directMessages: { ...s.directMessages, [convId]: msgs } })),
  prependDirectMessages: (convId, msgs) =>
    set((s) => ({
      directMessages: {
        ...s.directMessages,
        [convId]: [...msgs, ...(s.directMessages[convId] ?? [])],
      },
    })),
  appendDirectMessage: (convId, msg) =>
    set((s) => ({
      directMessages: {
        ...s.directMessages,
        [convId]: [...(s.directMessages[convId] ?? []), msg],
      },
    })),
  replaceDirectMessage: (convId, tempId, real) =>
    set((s) => ({
      directMessages: {
        ...s.directMessages,
        [convId]: (s.directMessages[convId] ?? []).map((m) => (m.id === tempId ? real : m)),
      },
    })),
  upsertDirectMessage: (convId, msg) =>
    set((s) => ({
      directMessages: {
        ...s.directMessages,
        [convId]: (s.directMessages[convId] ?? []).map((m) => (m.id === msg.id ? msg : m)),
      },
    })),
  tombstoneDirectMessage: (convId, messageId, deletedAt) =>
    set((s) => ({
      directMessages: {
        ...s.directMessages,
        [convId]: (s.directMessages[convId] ?? []).map((m) =>
          m.id === messageId ? { ...m, isDeleted: true, text: null, deletedAt } : m
        ),
      },
    })),
  addDirectReadReceipt: (convId, messageId, userId, readAt) =>
    set((s) => ({
      directMessages: {
        ...s.directMessages,
        [convId]: (s.directMessages[convId] ?? []).map((m) =>
          m.id === messageId
            ? { ...m, readBy: [...m.readBy.filter((r) => r.userId !== userId), { userId, readAt }] }
            : m
        ),
      },
    })),
  removeDirectMessage: (convId, id) =>
    set((s) => ({
      directMessages: {
        ...s.directMessages,
        [convId]: (s.directMessages[convId] ?? []).filter((m) => m.id !== id),
      },
    })),
  setDirectHasMore: (convId, v) =>
    set((s) => ({ directHasMore: { ...s.directHasMore, [convId]: v } })),

  // Direct unread
  setDirectUnread: (convId, n) =>
    set((s) => ({ directUnread: { ...s.directUnread, [convId]: n } })),
  incrementDirectUnread: (convId) =>
    set((s) => ({
      directUnread: { ...s.directUnread, [convId]: (s.directUnread[convId] ?? 0) + 1 },
    })),
  clearDirectUnread: (convId) => set((s) => ({ directUnread: { ...s.directUnread, [convId]: 0 } })),

  // WS
  setWsStatus: (wsStatus) => set({ wsStatus }),
  setWsFns: (fns) => set({ wsFns: fns }),

  // Reset — wipe all user-specific data on logout
  resetStore: () =>
    set({
      globalConversationId: null,
      globalMessages: [],
      globalHasMore: false,
      globalUnread: 0,
      isPanelOpen: false,
      participants: {},
      directConversations: [],
      activeDirectId: null,
      directMessages: {},
      directHasMore: {},
      directUnread: {},
      wsStatus: 'idle',
      wsFns: null,
    }),
}));
